// @file Surface store
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException } from '@@/bits/error_tracker'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { displayNameForUser } from '@@/bits/user_model'
import { Wall as WallApi } from '@@/dashboard/padlet_api'
import { ApiErrorCode, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useUserAccountsStore } from '@@/pinia/user_accounts_store'
import type {
  AccountMenuItem,
  Id,
  LibraryId,
  User,
  WallBackground,
  WallId,
  WallLayoutAttributes,
  WallViz,
} from '@@/types'
import { PostSize } from '@@/types'
import { useFreezePadlet } from '@@/vuecomposables/freeze_padlet_store'
import type { ConfirmationDialogPayload } from '@@/vuexstore/modules/global_confirmation_dialog'
import type { Wall } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const SEARCH_BAR_DEFAULT_TOP = 16

export const useSurfaceStore = defineStore('surface', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const userAccountsStore = useUserAccountsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const xSearchBar = ref(false)
  const isSearchBarInEditMode = ref(false)
  const searchBarTop = ref(SEARCH_BAR_DEFAULT_TOP)

  // Getters
  const isTemplate = computed<boolean>(() => surfaceVuexStore?.getters.isTemplate)
  const isMobileLayout = computed<boolean>(() => surfaceVuexStore?.getters.isMobileLayout)
  const isDesktopLayout = computed<boolean>(() => surfaceVuexStore?.getters.isDesktopLayout)
  const isScreenshotMode = computed<boolean>(() => surfaceVuexStore?.getters.isScreenshotMode)
  const isOnline = computed(() => surfaceVuexStore?.state.isOnline)
  const isOverlayVisible = computed<boolean>(() => surfaceVuexStore?.getters.isOverlayVisible ?? false)
  const wallId = computed(() => surfaceVuexStore?.state.wall.id)
  const xHeader = computed(() => surfaceVuexStore?.getters.xHeader)
  const isUserHostingZoomCollaboration = computed<boolean>(() => surfaceVuexStore?.state.isUserHostingZoomCollaboration)
  const headerHeight = computed<number>(() => surfaceVuexStore?.getters.headerHeight)
  const background = computed<WallBackground>(() => surfaceVuexStore?.getters.background)
  const postSize = computed<PostSize>(() => surfaceVuexStore?.getters.postSize)
  const isPostSizeWide = computed<boolean>(() => postSize.value === PostSize.WIDE)
  const isPostSizeStandard = computed<boolean>(() => postSize.value === PostSize.STANDARD)
  const isGrid = computed<boolean>(() => surfaceVuexStore?.getters.isGrid)
  const isMap = computed<boolean>(() => surfaceVuexStore?.getters.isMap)
  const isShelf = computed<boolean>(() => surfaceVuexStore?.getters.isShelf)
  const isStream = computed<boolean>(() => surfaceVuexStore?.getters.isStream)
  const isSectioned = computed<boolean>(() => surfaceVuexStore?.getters.isSectioned)
  const isTimeline = computed<boolean>(() => surfaceVuexStore?.getters.isTimeline)
  const isTimelineV1 = computed<boolean>(() => surfaceVuexStore?.getters.isTimelineV1)
  const isTimelineV2 = computed<boolean>(() => surfaceVuexStore?.getters.isTimelineV2)
  const isCanvas = computed<boolean>(() => surfaceVuexStore?.getters.isCanvas)
  const isCustomBackground = computed<boolean>(() => surfaceVuexStore?.getters.background.is_custom)
  const isBackgroundBlurred = computed<boolean>(() => surfaceVuexStore?.getters.isBackgroundBlurred)
  const isTitleBarTransparent = computed<boolean>(() => surfaceVuexStore?.getters.isTitleBarTransparent)
  const backgroundColor = computed(() => surfaceVuexStore?.getters.backgroundColor)
  const backgroundLuminance = computed<'light' | 'dark'>(() => surfaceVuexStore?.getters.backgroundLuminance)
  const colorScheme = computed<'light' | 'dark'>(() => surfaceVuexStore?.getters.colorScheme)
  const skinAccentColorAsRGB = computed(() => surfaceVuexStore?.getters.skinAccentColorAsRGB)
  const skinPrimaryColorAsRGB = computed(() => surfaceVuexStore?.getters.skinPrimaryColorAsRGB)
  const skinPrimaryVariantColorAsRGB = computed(() => surfaceVuexStore?.getters.skinPrimaryVariantColorAsRGB)
  const skinAccentTextColor = computed(() => surfaceVuexStore?.getters.skinAccentTextColor)
  const isReadOnly = computed(() => surfaceVuexStore?.getters.isReadOnly)
  const isEmbedded = computed<boolean>(() => surfaceVuexStore?.state.isEmbedded)
  const dir = computed<'ltr' | 'rtl'>(() => surfaceVuexStore?.getters.dir)
  const fonts = computed(() => {
    // in SettingsV2, we want the fonts to be in a different order
    const fontIdOrder = [3, 2, 4, 1]
    const resultFonts = fontIdOrder.map((id) =>
      surfaceVuexStore?.state.constants.allFonts.find((font) => font.id === id),
    )
    return resultFonts
  })
  const fontId = computed(() => surfaceVuexStore?.getters.fontId)
  const viz = computed<WallViz>(() => surfaceVuexStore?.getters.viz)
  const format = computed<WallViz>(() => surfaceVuexStore?.getters.format)
  const newPostLocation = computed<'top' | 'bottom'>(() => surfaceVuexStore?.getters.newPostLocation)
  const address = computed<string>(() => surfaceVuexStore?.getters.address)
  const builder = computed<User>(() => surfaceVuexStore?.getters.builder)
  const title = computed<string>(() => surfaceVuexStore?.getters.title)
  const description = computed<string | undefined>(() => surfaceVuexStore?.getters.description)
  const portrait = computed<string>(() => surfaceVuexStore?.getters.portrait)
  const wallCreatedAt = computed<string>(() => surfaceVuexStore?.getters.wallCreatedAt)
  const wallUpdatedAt = computed<string>(() => surfaceVuexStore?.getters.wallUpdatedAt)
  const wallAttributes = computed<Partial<Wall>>(() => surfaceVuexStore?.state.wall)
  const mapThemeId = computed<number>(() => surfaceVuexStore?.state.wall.theme_id)
  const isRefreshPending = computed<boolean>(() => surfaceVuexStore?.state.isRefreshPending)
  const filterProfanity = computed<boolean>(() => surfaceVuexStore?.getters.filterProfanity)
  const domainName = computed<string>(() => surfaceVuexStore?.getters.domainName)
  const namespace = computed<string>(() => surfaceVuexStore?.getters.namespace)
  const publicKey = computed<string>(() => surfaceVuexStore?.getters.publicKey)
  const googleApiKeyForDrive = computed<string>(() => surfaceVuexStore?.state.constants.googleApiKeyForDrive)
  const googleAppId = computed<string>(() => surfaceVuexStore?.state.constants.googleAppId)
  const permalink = computed<string>(() => surfaceVuexStore?.getters.name)
  const links = computed<Record<string, string>>(() => surfaceVuexStore?.getters.links)
  const xReportPanel = computed<boolean>(() => surfaceVuexStore?.getters.xReportPanel)
  const reportedPostId = computed<Id | null>(() => surfaceVuexStore?.getters.reportedPostId)
  const coverPostId = computed<Id | null>(() => surfaceVuexStore?.getters.coverPostId)
  const xAuthor = computed<boolean>(() => surfaceVuexStore?.getters.xAuthor)
  const xCommentsUnderPosts = computed<boolean>(
    () =>
      surfaceVuexStore?.getters.isMap === false &&
      surfaceVuexStore?.getters.isTable === false &&
      surfaceVuexStore?.getters.isCanvas === false &&
      surfaceVuexStore?.getters.isTimeline === false,
  )

  const canUseSections = computed<boolean>(() => surfaceVuexStore?.getters.sectionableLayouts.includes(viz.value))
  const isUsedInEducationalContext = computed<boolean>(() => surfaceVuexStore?.getters.isUsedInEducationalContext)
  const user = computed<User>(() => surfaceVuexStore?.getters.user)
  const xFilterProfanitySetting = computed<boolean>(() => surfaceVuexStore?.getters.xFilterProfanitySetting ?? false)
  const allLayouts = computed(() => surfaceVuexStore?.state.constants.allLayouts)
  const allLayoutsByGroup = computed<{
    simple: WallLayoutAttributes[]
    advanced: WallLayoutAttributes[]
  }>(() => surfaceVuexStore?.state.constants.allLayoutsByGroup)
  const breakoutSectionId = computed(() => surfaceVuexStore?.state.breakoutSectionId)
  const isSectionBreakout = computed<boolean>(() => breakoutSectionId.value !== null)
  const isSubmissionRequest = computed<boolean>(() => surfaceVuexStore?.state.isSubmissionRequest)
  const isSlideshow = computed<boolean>(() => surfaceVuexStore?.getters.isSlideshow)
  const blockedContentProviders = computed<Set<string>>(() => surfaceVuexStore?.getters.blockedContentProviders)

  // Actions

  /**
   * Open the search bar.
   * To use where updating xSearchBar directly is not possible (e.g. in a Vue 2 component).
   */
  function openSearchBar(): void {
    xSearchBar.value = true
  }

  function updateSearchBarTop(top: number): void {
    searchBarTop.value = top
  }

  function refresh({ isAutoRefresh }: { isAutoRefresh?: boolean }): void {
    void surfaceVuexStore?.dispatch('refresh', { isAutoRefresh })
  }

  function changeConnectivity(isOnline: boolean): void {
    void surfaceVuexStore?.dispatch('changeConnectivity', { isOnline })
  }

  function hideReportPanel(): void {
    void surfaceVuexStore?.dispatch('hideReportPanel')
  }

  function hideDevelopersPanel(): void {
    void surfaceVuexStore?.dispatch('hideDevelopersPanel')
  }

  function showOverFileSizeModal(file): void {
    void surfaceVuexStore?.dispatch('showOverFileSizeModal', { file }, { root: true })
  }

  function showPromptToLoginDialog(payload: Partial<ConfirmationDialogPayload>): void {
    void surfaceVuexStore?.dispatch('showPromptToLoginDialog', payload)
  }

  function showContributorsList(): void {
    void surfaceVuexStore?.dispatch('showContributorsList')
  }

  // libraries
  const isLibraryWall = computed<boolean>(() => surfaceVuexStore?.getters.isLibraryWall)

  // tenants
  const isNativeTenant = computed<boolean>(() => {
    return wallAttributes.value.tenant_id === 1
  })

  // frozen state
  const { isFrozen, toggleFreezePadlet, handlePadletFrozen, handlePadletUnfrozen } = useFreezePadlet(
    wallAttributes,
    surfaceVuexStore,
  )

  // padlets in a personal library that's over quota are always frozen
  // similar logic to wall.rb#is_frozen?
  const isFrozenDueToOverQuota = computed(
    () => isNativeTenant.value && !isLibraryWall.value && builder.value.days_before_freeze === 0,
  )

  // templates
  function markWallAsTemplate(): void {
    void surfaceVuexStore?.commit('MARK_WALL_AS_TEMPLATE')
  }

  function unmarkWallAsTemplate(): void {
    void surfaceVuexStore?.commit('UNMARK_WALL_AS_TEMPLATE')
  }

  // Transfer Wall
  const xTransferWallDialog = ref<boolean>(false)

  const transferableAccounts = computed<AccountMenuItem[]>(() => {
    return userAccountsStore.accountsMenuList.filter((account) => account.libraryId !== wallAttributes.value.library_id)
  })

  function openTransferWallDialog(): void {
    xTransferWallDialog.value = true
  }

  function closeTransferWallDialog(): void {
    xTransferWallDialog.value = false
  }

  async function transferWall(wallId: WallId, destinationLibraryId: LibraryId | null): Promise<void> {
    const libraryName =
      destinationLibraryId == null
        ? displayNameForUser(user.value)
        : userAccountsStore.librariesById[destinationLibraryId].name

    try {
      await WallApi.transfer({
        wallId,
        destinationLibraryId,
      })

      globalSnackbarStore.setSnackbar({
        message: __('Padlet transferred to %{libraryName}', {
          libraryName,
        }),
        shouldTruncate: true,
        notificationType: SnackbarNotificationType.success,
        timeout: 2500,
      })

      reload()
    } catch (e) {
      const parsedErrorMessage = JSON.parse(e.message)
      const errorCode = parsedErrorMessage.errors[0].code

      if (errorCode === ApiErrorCode.EXCEEDS_WALL_QUOTA_LIMIT) {
        globalSnackbarStore.setSnackbar({
          message: __("You've hit the padlet quota for %{libraryName}", {
            libraryName,
          }),
          shouldTruncate: true,
          timeout: 2500,
          notificationType: SnackbarNotificationType.error,
        })
        return
      }

      globalSnackbarStore.setSnackbar({
        message: __('Padlet could not be transferred to %{libraryName}', {
          libraryName,
        }),
        shouldTruncate: true,
        timeout: 2500,
        notificationType: SnackbarNotificationType.error,
      })

      if (errorCode !== ApiErrorCode.NO_DESTINATION_PERMISSION) {
        captureFetchException(e, { source: 'SurfaceTransferWall' })
      }
    }

    trackEvent('Transfer padlet', 'Clicked from transfer wall modal on surface web')
  }

  return {
    // State
    xSearchBar,
    isSearchBarInEditMode,
    searchBarTop,
    isRefreshPending,
    xTransferWallDialog,

    // Getters
    isTemplate,
    isMobileLayout,
    isDesktopLayout,
    isScreenshotMode,
    isOverlayVisible,
    isUserHostingZoomCollaboration,
    wallId,
    xHeader,
    headerHeight,
    background,
    isGrid,
    isMap,
    isShelf,
    isSectioned,
    isStream,
    isTimeline,
    isTimelineV1,
    isTimelineV2,
    isCanvas,
    isCustomBackground,
    isBackgroundBlurred,
    isTitleBarTransparent,
    backgroundColor,
    skinAccentColorAsRGB,
    skinPrimaryColorAsRGB,
    skinPrimaryVariantColorAsRGB,
    skinAccentTextColor,
    backgroundLuminance,
    colorScheme,
    isReadOnly,
    isFrozen,
    isFrozenDueToOverQuota,
    isEmbedded,
    dir,
    fonts,
    fontId,
    viz,
    format,
    newPostLocation,
    address,
    builder,
    title,
    description,
    portrait,
    wallAttributes,
    mapThemeId,
    filterProfanity,
    domainName,
    namespace,
    googleApiKeyForDrive,
    googleAppId,
    publicKey,
    permalink,
    links,
    canUseSections,
    isOnline,
    xReportPanel,
    reportedPostId,
    coverPostId,
    xAuthor,
    xCommentsUnderPosts,
    isUsedInEducationalContext,
    wallCreatedAt,
    wallUpdatedAt,
    user,
    xFilterProfanitySetting,
    allLayouts,
    allLayoutsByGroup,
    postSize,
    isPostSizeWide,
    isPostSizeStandard,
    breakoutSectionId,
    isSectionBreakout,
    isSubmissionRequest,
    isSlideshow,
    blockedContentProviders,
    transferableAccounts,

    // Actions
    openSearchBar,
    updateSearchBarTop,
    refresh,
    changeConnectivity,
    handlePadletFrozen,
    handlePadletUnfrozen,
    hideReportPanel,
    hideDevelopersPanel,
    showOverFileSizeModal,
    showPromptToLoginDialog,
    toggleFreezePadlet,
    showContributorsList,
    markWallAsTemplate,
    unmarkWallAsTemplate,
    openTransferWallDialog,
    closeTransferWallDialog,
    transferWall,
  }
})
